import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";

import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";

import FormField from "components/account/FormField";

import DataTable from "parts/Tables/DataTable";

import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";

import { getTasks, createTask, deleteTaskById, editTask, type Task } from "@shared/api/tasks";
import { getAiPlans, type AiPlan } from "@shared/api/aiPlans";

// 必須項目であることを示すラベルコンポーネント
const RequiredLabel = ({ label }: { label: string }) => {
  // コンポーネント内で独立して t を取得する
  const { t } = useContext(LanguageContext) as { t: (k: string) => string };

  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      {label}
      <Chip
        label={t("Task.Required") || "必須"}
        color="error"
        size="small"
        sx={{ ml: 1, height: "18px", fontSize: "0.65rem", padding: "0 4px" }}
      />
    </span>
  );
};

function TaskManagerTable() {
  const { t } = useContext(LanguageContext) as { t: (k: string) => string };
  const { session } = useSessionContext();

  const [tasks, setTasks] = useState<Task[]>([]);
  const [plans, setPlans] = useState<AiPlan[]>([]);
  
  // フィルター用ステート
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<number | "all">("all");
  const [filterSource, setFilterSource] = useState<string>("all");

  const defaultCategory = t("Task.Uncategorized") || "未分類";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState<Omit<Task, "id">>({
    title: "",
    category: defaultCategory,
    task_type: "",
    detail: "",
    memo: null,
    priority: 10,
    status: 0,
    pomodoro_count: 0,
    do_comment: null,
    planned_start: "",
    deadline: "",
    estimated_duration: null,
    worktime: null,
    completion_rate: null,
    is_ai_created: false,
    ai_plans_id: null,
  });

  const [formErrors, setFormErrors] = useState<{
    title?: string;
    category?: string;
    priority?: string;
  }>({});
  
  const [alert, setAlert] = useState<{
    message: string;
    color: "success" | "error" | "warning" | "info";
  } | null>(null);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTaskId(null);
    setFormErrors({});
    setTaskForm({
      title: "",
      category: defaultCategory,
      task_type: "",
      detail: "",
      memo: null,
      priority: 10,
      status: 0,
      pomodoro_count: 0,
      do_comment: null,
      planned_start: "",
      deadline: "",
      estimated_duration: null,
      worktime: null,
      completion_rate: null,
      is_ai_created: false,
      ai_plans_id: null,
    });
  };

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<(Task | null)>(null);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);

  const numericFields: (keyof typeof taskForm)[] = [
    "priority",
    "status",
    "pomodoro_count",
    "completion_rate",
    "ai_plans_id",
  ];

  const handleOpenDetailDialog = (task: Task) => {
    setSelectedTask(task);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedTask(null);
  };

  const handleChange = (field: keyof typeof taskForm, value: any) => {
    const newValue = numericFields.includes(field)
      ? value === "" || isNaN(Number(value))
        ? null
        : Number(value)
      : value;

    setTaskForm((prev) => ({
      ...prev,
      [field]: newValue,
    }));

    // 入力されたら対象のエラーを消す
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleRegister = async () => {
    // 必須項目のバリデーションチェック
    const errors: typeof formErrors = {};
    if (!taskForm.title?.trim()) errors.title = t("Task.error.title");
    if (!taskForm.category?.trim()) errors.category = t("Task.error.category");
    if (taskForm.priority === undefined || taskForm.priority === null) errors.priority = t("Task.error.priority");

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setAlert({ message: t("Task.error.Invalid"), color: "error" });
      return;
    }

    if (!session?.access_token) return;

    try {
      if (editingTaskId !== null) {
        // 更新時
        const updated = await editTask(session.access_token, editingTaskId, taskForm);
        setTasks(tasks.map((task) => (task.id === editingTaskId ? updated : task)));
        setAlert({ message: t("Task.UpdateSuccess"), color: "success" });
      } else {
        // 新規作成時
        const newTask = await createTask(session.access_token, taskForm);
        setTasks([...tasks, newTask]);
        setAlert({ message: t("Task.SuccessMessage"), color: "success" });
      }

      setTimeout(() => setAlert(null), 3000);
      setDialogOpen(false);
      setEditingTaskId(null);
      setFormErrors({});
    } catch (err) {
      console.error("登録/更新失敗", err);
      setAlert({
        message: editingTaskId !== null ? t("Task.UpdateError") : t("Task.RegisterError"),
        color: "error",
      });
    }
  };

  const deleteTask = async (id: number) => {
    if (!session?.access_token) return;

    try {
      await deleteTaskById(session.access_token, id);
      setTasks(tasks.filter((task) => task.id !== id));
      setAlert({ message: t("Task.DeleteSuccess"), color: "info" });
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      console.error("削除失敗", err);
      setAlert({ message: t("Task.DeleteError"), color: "error" });
    }
  };

  // タスクとプランを同時に取得。組み直しの反映・取り消し時も一覧を即時更新する。
  const fetchData = useCallback(async (hideGlobalLoader = false) => {
    try {
      if (!session?.access_token) return;

      const [tasksData, plansData] = await Promise.all([
        getTasks(session.access_token, { hideGlobalLoader }),
        getAiPlans(session.access_token, { hideGlobalLoader }),
      ]);

      console.log(`fetch get getTasks.length::${tasksData?.length}`);
      setTasks(tasksData);
      setPlans(plansData || []);
    } catch (err) {
      console.error("データの取得に失敗:", err);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchData();
    const refreshAfterRecovery = () => fetchData(true);
    window.addEventListener("restart-route-applied", refreshAfterRecovery);
    return () => window.removeEventListener("restart-route-applied", refreshAfterRecovery);
  }, [fetchData]);

  // 動的な多言語ステータスマップ
  const statusMap = useMemo<Record<number, string>>(() => ({
    0: t("Task.StatusMap.NotStarted") || "未着手",
    1: t("Task.StatusMap.InProgress") || "進行中",
    2: t("Task.StatusMap.Completed") || "完了",
  }), [t]);

  // 動的な多言語優先度マップ
  const priorityMap = useMemo<Record<number, string>>(() => ({
    10: t("Task.Priorities.High"),
    20: t("Task.Priorities.Medium"),
    30: t("Task.Priorities.Low"),
  }), [t]);

  const reversePriorityMap = useMemo<Record<string, number>>(() => ({
    [t("Task.Priorities.High")]: 10,
    [t("Task.Priorities.Medium")]: 20,
    [t("Task.Priorities.Low")]: 30,
  }), [t]);

  // タスクに紐づくAIプランIDを抽出し、昇順で並べた配列を作成
  const aiPlanIds = useMemo(() => {
    const ids = tasks
      .filter((t) => t.is_ai_created && t.ai_plans_id != null)
      .map((t) => t.ai_plans_id as number);
    return Array.from(new Set(ids)).sort((a, b) => a - b);
  }, [tasks]);

  // タスクから表示用ラベル（「手動」「AI-1」「AI-2」等）を取得する関数
  const getSourceLabel = useCallback((task: Task) => {
    const manualStr = t("Task.SourceType.Manual") || "手動";
    const aiStr = t("Task.SourceType.AI") || "AI";

    if (!task.is_ai_created) return manualStr;
    if (task.ai_plans_id == null) return aiStr;
    const idx = aiPlanIds.indexOf(task.ai_plans_id);
    return idx !== -1 ? `${aiStr}-${idx + 1}` : aiStr;
  }, [aiPlanIds, t]);

  const sourceOptions = useMemo(() => {
    const labels = new Set(tasks.map((task) => getSourceLabel(task)));
    const options = [{ label: t("Task.AllSource") || "すべて", value: "all" }];
    const manualStr = t("Task.SourceType.Manual") || "手動";
    
    // 「手動」は必ず選択肢に入れる
    if (labels.has(manualStr)) {
      labels.delete(manualStr);
    }
    options.push({ label: manualStr, value: manualStr });

    // 残りの AI-1, AI-2 などを追加
    Array.from(labels)
      .sort()
      .forEach((label) => {
        options.push({ label, value: label });
      });

    return options;
  }, [tasks, getSourceLabel, t]);

  // フィルタリング処理（全項目対応版）
  const filteredTasks = useMemo(() => {
    const kw = searchKeyword.toLowerCase().trim();

    return tasks.filter((task) => {
      // ステータスでの絞り込み
      const matchStatus = filterStatus === "all" || task.status === filterStatus;
      if (!matchStatus) return false;

      const sourceLabel = getSourceLabel(task);
      const matchSource = filterSource === "all" || sourceLabel === filterSource;
      if (!matchSource) return false;

      // 検索キーワードが空ならすべて表示
      if (!kw) return true;

      // 検索用に表示テキストを取得
      const priorityLabel = priorityMap[task.priority] ?? "";
      const statusLabel = statusMap[task.status] ?? "";

      // いずれかの項目にキーワードが含まれていれば true を返す
      return (
        task.id.toString().includes(kw) ||
        (task.title || "").toLowerCase().includes(kw) ||
        (task.category || "").toLowerCase().includes(kw) ||
        (task.detail || "").toLowerCase().includes(kw) ||
        (task.memo || "").toLowerCase().includes(kw) ||
        (task.do_comment || "").toLowerCase().includes(kw) ||
        (task.planned_start || "").includes(kw) ||
        (task.deadline || "").includes(kw) ||
        priorityLabel.toLowerCase().includes(kw) ||
        statusLabel.toLowerCase().includes(kw) ||
        sourceLabel.toLowerCase().includes(kw)
      );
    });
  }, [tasks, searchKeyword, filterStatus, filterSource, priorityMap, statusMap, getSourceLabel]);

  const columns = [
    { Header: "ID", accessor: "id", align: "left", sortable: true, width: "50px" },
    {
      Header: t("Task.Category"),
      accessor: "category",
      align: "left",
      sortable: true,
      width: "50px",
    },
    { Header: t("Task.Name"), accessor: "title", align: "left", sortable: true },
    {
      Header: t("Task.PlannedStart"),
      accessor: "planned_start",
      align: "center",
      sortable: true,
      Cell: ({ value }: { value: string }) => (value ? dayjs(value).format("YYYY-MM-DD") : "-"),
      width: "50px",
    },
    {
      Header: t("Task.DueDate"),
      accessor: "deadline",
      align: "center",
      sortable: true,
      sortFunction: (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime(),
      Cell: ({ value }: { value: string }) => (value ? dayjs(value).format("YYYY-MM-DD") : "-"),
      width: "50px",
    },
    {
      Header: t("Task.Priority"),
      accessor: "priority",
      align: "center",
      sortable: true,
      sortFunction: (a: number, b: number) => a - b,
      Cell: ({ value }: { value: number }) => priorityMap[value] ?? value,
      width: "80px",
    },
    {
      Header: t("Task.Status"),
      accessor: "status",
      align: "center",
      sortable: true,
      Cell: ({ value }: { value: number }) => statusMap[value] ?? value,
      width: "80px",
    },
    {
      Header: t("Task.Source"),
      accessor: "source",
      align: "center",
      sortable: true,
      Cell: ({ row }: { row: any }) => getSourceLabel(row.original),
      width: "80px",
    },
    {
      Header: t("Task.Operation"),
      accessor: "actions",
      align: "center",
      Cell: ({ row }: { row: any }) => (
        <>
          <IconButton onClick={() => handleOpenDetailDialog(row.original)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => {
              const task = row.original;
                setTaskForm({
                  ...task,
                  // 日付フォーマットを YYYY-MM-DD に変換してからセット
                  planned_start: task.planned_start ? dayjs(task.planned_start).format("YYYY-MM-DD") : "",
                  deadline: task.deadline ? dayjs(task.deadline).format("YYYY-MM-DD") : "",
                });
                setEditingTaskId(task.id);
                setDialogOpen(true);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={() => deleteTask(row.original.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  const rows = filteredTasks.map((task) => ({
    ...task,
    actions: "",
  }));

  return (
    <MDBox>
      {alert && (
        <MDAlert color={alert.color} dismissible>
          {alert.message}
        </MDAlert>
      )}

      {/* フィルター及び追加ボタンのコンテナ */}
      <Grid container spacing={2} justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={8} display="flex" gap={2} flexWrap="wrap">
          {/* キーワード検索 */}
          <TextField
            label={t("Task.Search")}
            variant="outlined"
            size="small"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            sx={{ minWidth: 260 }}
            placeholder={t("Task.SearchPlaceholder") || "タスク名、カテゴリ、詳細など"}
          />
          {/* ステータスフィルター */}
          <Autocomplete
            size="small"
            options={[
              { label: t("Task.AllStatus") || "すべて", value: "all" },
              ...Object.entries(statusMap).map(([k, v]) => ({
                label: v,
                value: Number(k),
              })),
            ]}
            value={
              filterStatus === "all"
                ? { label: t("Task.AllStatus") || "すべて", value: "all" }
                : { label: statusMap[filterStatus], value: filterStatus }
            }
            onChange={(_, option) => setFilterStatus(option ? (option.value as number | "all") : "all")}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            renderInput={(params) => (
              <TextField {...params} label={t("Task.Status")} variant="outlined" />
            )}
            sx={{ minWidth: 160 }}
          />

          {/* 作成元（手動/AI-1...）のフィルター */}
          <Autocomplete
            size="small"
            options={sourceOptions}
            value={sourceOptions.find((opt) => opt.value === filterSource) || sourceOptions[0]}
            onChange={(_, option) => setFilterSource(option ? option.value : "all")}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            renderInput={(params) => (
              <TextField {...params} label={t("Task.Source") || "作成元"} variant="outlined" />
            )}
            sx={{ minWidth: 160 }}
          />
        </Grid>

        <Grid item xs={12} md={4} display="flex" justifyContent="flex-end">
          <MDButton variant="gradient" color="info" onClick={handleOpenDialog}>
            {t("Task.Add")}
          </MDButton>
        </Grid>
      </Grid>

      <DataTable
        table={{ columns, rows }}
        isSorted={true}
        entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50] }}
        showTotalEntries={true}
        noEndBorder
      />

      {/* 編集・追加ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pt: 1.5, pb: 0, px: 3, fontSize: "1.25rem" }}>
          {editingTaskId !== null ? t("Task.EditDialogTitle") : t("Task.DialogTitle")}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: "8px !important", pb: 1 }}>
          <Grid container spacing={1.5}>
            {/* プラン選択（任意） */}
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={plans}
                getOptionLabel={(option) => option.title || option.category || `Plan ${option.id}`}
                value={plans.find((p) => p.id === taskForm.ai_plans_id) || null}
                onChange={(_, newValue) => handleChange("ai_plans_id", newValue ? newValue.id : null)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Task.Plan") || "AIプラン"}
                    placeholder={t("Task.AiPlan") || "プランを選択（任意）"}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField
                fullWidth
                label={<RequiredLabel label={t("Task.Name")} />}
                value={taskForm.title}
                onChange={(e: any) => handleChange("title", e.target.value)}
                error={!!formErrors.title}
              />
              {formErrors.title && (
                <MDTypography variant="caption" color="error" fontWeight="medium" pl={1}>
                  {formErrors.title}
                </MDTypography>
              )}
            </Grid>
            <Grid item xs={6}>
              <FormField
                fullWidth
                label={<RequiredLabel label={t("Task.Category")} />}
                value={taskForm.category}
                onChange={(e: any) => handleChange("category", e.target.value)}
                error={!!formErrors.category}
              />
              {formErrors.category && (
                <MDTypography variant="caption" color="error" fontWeight="medium" pl={1}>
                  {formErrors.category}
                </MDTypography>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormField
                fullWidth
                label={t("Task.Detail")}
                multiline
                minRows={4}
                value={taskForm.detail}
                onChange={(e: any) => handleChange("detail", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormField
                fullWidth
                label={t("Task.Memo")}
                multiline
                minRows={2}
                value={taskForm.memo ?? ""}
                onChange={(e: any) => handleChange("memo", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormField
                fullWidth
                label={t("Task.DoComment")}
                multiline
                minRows={2}
                value={taskForm.do_comment ?? ""}
                onChange={(e: any) => handleChange("do_comment", e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormField
                fullWidth
                label={t("Task.PlannedStart")}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={taskForm.planned_start}
                onChange={(e: any) => handleChange("planned_start", e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <FormField
                fullWidth
                label={t("Task.DueDate")}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={taskForm.deadline}
                onChange={(e: any) => handleChange("deadline", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                size="small"
                options={[
                  t("Task.Priorities.High"),
                  t("Task.Priorities.Medium"),
                  t("Task.Priorities.Low"),
                ]}
                value={priorityMap[taskForm.priority] ?? t("Task.Priorities.Medium")}
                onChange={(_, value) =>
                  handleChange("priority", reversePriorityMap[value ?? ""] ?? 20)
                }
                isOptionEqualToValue={(option, value) => option === value}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={<RequiredLabel label={t("Task.Priority")} />} 
                    variant="outlined" 
                    error={!!formErrors.priority}
                  />
                )}
              />
              {formErrors.priority && (
                <MDTypography variant="caption" color="error" fontWeight="medium" pl={1}>
                  {formErrors.priority}
                </MDTypography>
              )}
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                size="small"
                options={Object.entries(statusMap).map(([k, v]) => ({
                  label: v,
                  value: Number(k),
                }))}
                value={{
                  label: statusMap[taskForm.status],
                  value: taskForm.status,
                }}
                onChange={(_, option) => {
                  if (option) handleChange("status", option.value);
                }}
                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                renderInput={(params) => (
                  <TextField {...params} label={t("Task.Status")} variant="outlined" />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <FormField
                fullWidth
                label={t("Task.EstimatedDuration")}
                placeholder={t("Task.Example") + ": 01:30:00"}
                value={taskForm.estimated_duration || ""}
                onChange={(e: any) => handleChange("estimated_duration", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <FormField
                fullWidth
                label={t("Task.Worktime")}
                placeholder={t("Task.Example") + ": 00:45:00"}
                value={taskForm.worktime || ""}
                onChange={(e: any) => handleChange("worktime", e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <FormField
                fullWidth
                label={t("Task.CompletionRate")}
                type="number"
                inputProps={{ step: "0.01", min: "0", max: "1" }}
                InputLabelProps={{ shrink: true }}
                value={taskForm.completion_rate || 0}
                onChange={(e: any) => handleChange("completion_rate", e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton variant="gradient" color="info" onClick={handleCloseDialog}>
            {t("Task.Cancel")}
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleRegister}>
            {editingTaskId !== null ? t("Task.UpdateButton") : t("Task.Register")}
          </MDButton>
        </DialogActions>
      </Dialog>
      
      {/* タスク詳細表示ダイアログ */}
      <Dialog open={detailDialogOpen} onClose={handleCloseDetailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t("Task.DetailTitle")}</DialogTitle>
        <DialogTitle sx={{ pt: 1.5, pb: 0, px: 3, fontSize: "1.25rem" }}>
          {t("Task.DetailTitle")}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ maxWidth: "600px", wordBreak: "break-word", whiteSpace: "pre-wrap" }}
        >
          {selectedTask && (
            <>
              {/* プランの表示 */}
              <MDBox display="flex" mb={2}>
                <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                  {t("Task.Plan") || "AIプラン"}:
                </MDTypography>
                <MDTypography variant="body5" sx={{ flex: 1 }}>
                  {plans.find((p) => p.id === selectedTask.ai_plans_id)?.title || "-"}
                </MDTypography>
              </MDBox>

              <MDBox display="flex" mb={2}>
                <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                  {t("Task.Name")}:
                </MDTypography>
                <MDTypography variant="body5" sx={{ flex: 1 }}>
                  {selectedTask.title}
                </MDTypography>
              </MDBox>

              <MDBox display="flex" mb={2}>
                <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                  {t("Task.Category")}:
                </MDTypography>
                <MDTypography variant="body5" sx={{ flex: 1 }}>
                  {selectedTask.category}
                </MDTypography>
              </MDBox>

              <MDBox display="flex" mb={2}>
                <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                  {t("Task.Detail")}:
                </MDTypography>
                <MDTypography variant="body5" sx={{ flex: 1 }}>
                  {selectedTask.detail}
                </MDTypography>
              </MDBox>

              <MDBox display="flex" mb={2}>
                <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                  {t("Task.Memo")}:
                </MDTypography>
                <MDTypography variant="body5" sx={{ flex: 1 }}>
                  {selectedTask.memo ?? "-"}
                </MDTypography>
              </MDBox>

              <MDBox display="flex" mb={2}>
                <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                  {t("Task.DoComment")}:
                </MDTypography>
                <MDTypography variant="body5" sx={{ flex: 1 }}>
                  {selectedTask.do_comment ?? "-"}
                </MDTypography>
              </MDBox>

              <MDBox display="flex" mb={2}>
                <MDBox display="flex" flex={1}>
                  <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                    {t("Task.PlannedStart")}:
                  </MDTypography>
                  <MDTypography variant="body5">
                    {selectedTask.planned_start ? dayjs(selectedTask.planned_start).format("YYYY-MM-DD") : "-"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" flex={1}>
                  <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                    {t("Task.DueDate")}:
                  </MDTypography>
                  <MDTypography variant="body5">
                    {selectedTask.deadline ? dayjs(selectedTask.deadline).format("YYYY-MM-DD") : "-"}
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDBox display="flex" mb={2}>
                <MDBox display="flex" flex={1}>
                  <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                    {t("Task.Priority")}:
                  </MDTypography>
                  <MDTypography variant="body5">{priorityMap[selectedTask.priority]}</MDTypography>
                </MDBox>
                <MDBox display="flex" flex={1}>
                  <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                    {t("Task.Status")}:
                  </MDTypography>
                  <MDTypography variant="body5">{statusMap[selectedTask.status]}</MDTypography>
                </MDBox>
              </MDBox>

              <MDBox display="flex" mb={2}>
                <MDBox display="flex" flex={1}>
                  <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                    {t("Task.EstimatedDuration")}:
                  </MDTypography>
                  <MDTypography variant="body5">
                    {selectedTask.estimated_duration ?? "-"}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" flex={1}>
                  <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                    {t("Task.Worktime")}:
                  </MDTypography>
                  <MDTypography variant="body5">{selectedTask.worktime ?? "-"}</MDTypography>
                </MDBox>
              </MDBox>

              <MDBox display="flex" mb={2}>
                <MDBox display="flex" flex={1}>
                  <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                    {t("Task.CompletionRate")}:
                  </MDTypography>
                  <MDTypography variant="body5">
                    {(Number(selectedTask.completion_rate) || 0).toFixed(2)}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" flex={1}>
                  <MDTypography sx={{ minWidth: 100 }} variant="body4" fontWeight="bold">
                    {t("Task.Source")}:
                  </MDTypography>
                  <MDTypography variant="body5">
                    {getSourceLabel(selectedTask)}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="gradient" color="info" onClick={handleCloseDetailDialog}>
            {t("Task.Close")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

export default TaskManagerTable;